import { type ReactElement, useCallback, useMemo } from "react";
import type { Task } from "@common/types/task";
import type { TaskView } from "@common/types/views/tasksView";
import type { NamedView } from "@common/types/views/views";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import type { UseViewCollectionReturn } from "@hooks/view/useViewCollection";
import {
    getOpinionatedViewEmptyState,
    hasNarrowedTaskView,
} from "./opinionatedViewEmptyState";

interface UseTaskViewEmptyStateArgs {
    /** Null while the tasks load. */
    tasks: readonly Task[] | null;
    /** True while `tasks` are the previous query's, e.g. the view picked before. */
    isShowingPreviousTasks: boolean;
    /** True when any tasks failed to load, so empty tasks may not mean an empty view. */
    hasTaskLoadError: boolean;
    /** The page's full view, whose filters follow the page's. */
    view: TaskView;
    /** The page's search, which views don't store. */
    search: string | undefined;
    viewCollection: Pick<
        UseViewCollectionReturn<TaskView>,
        "baseView" | "setBaseViewId"
    >;
    builtInViews: ReadonlyArray<NamedView<TaskView>>;
    /** Applies a picked view, as the views menu does. */
    onViewSelect: (view: TaskView) => void;
}

/**
 * The empty state List and Board show in place of their groups when the
 * picked opinionated view has no tasks for its own filters. Null while the
 * tasks load or show the previous query's, after a failed load, when there
 * are tasks, or when the user narrowed the view, which keeps the page's
 * "No tasks match these filters".
 */
export const useTaskViewEmptyState = ({
    tasks,
    isShowingPreviousTasks,
    hasTaskLoadError,
    view,
    search,
    viewCollection: { baseView, setBaseViewId },
    builtInViews,
    onViewSelect,
}: UseTaskViewEmptyStateArgs): ReactElement | null => {
    const emptyState = useMemo(() => {
        if (
            tasks === null ||
            isShowingPreviousTasks ||
            hasTaskLoadError ||
            tasks.length > 0 ||
            baseView === null
        ) {
            return null;
        }
        if (
            hasNarrowedTaskView({
                filters: view.filters,
                viewFilters: baseView.filters,
                search,
            })
        ) {
            return null;
        }
        return getOpinionatedViewEmptyState({
            viewId: baseView.id,
            builtInViewIds: builtInViews.map(({ id }) => id),
        });
    }, [
        tasks,
        isShowingPreviousTasks,
        hasTaskLoadError,
        baseView,
        view.filters,
        search,
        builtInViews,
    ]);

    const actionView = builtInViews.find(
        ({ id }) => id === emptyState?.action?.viewId,
    );
    const selectActionView = useCallback(() => {
        if (actionView === undefined) {
            return;
        }
        setBaseViewId(actionView.id);
        onViewSelect(actionView);
    }, [actionView, setBaseViewId, onViewSelect]);

    return useMemo(
        () =>
            emptyState && (
                <EmptyState
                    header={emptyState.header}
                    ctaText={emptyState.action?.label}
                    action={actionView ? selectActionView : undefined}
                />
            ),
        [emptyState, actionView, selectActionView],
    );
};
